/** @jsx jsx */
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useField } from 'formik';
import { jsx } from '@emotion/core';

import { ChallengesType, IChallengeListProductItem } from 'interfaces';
import { productCurrencySelector } from 'reducers/app.reducer';
import { getProductFieldId, getResultUnit, sumAll } from 'utils/';
import { STEPS } from '../constants';
import Layout from '../StepLayout';
import { FinalStepOverview } from './FinalStepOverview';
import { TProductDetails } from 'interfaces/challenge.types';

/*  here the calcul of rewards and results is carried out by the frontend
    because we are in the case of a preview */
const getProductRewardValuePreview = (
  quantity: number,
  threshold: number | null,
  reward: number | null,
): number =>
  threshold === null || reward === null || quantity < threshold
    ? 0
    : quantity * reward;

const getProductResultValuePreview = (
  coefficient: number | null,
  declaredQuantity: number,
) => {
  if (!coefficient || !declaredQuantity) return 0;
  return coefficient * declaredQuantity;
};

export const DeclarationFormFinalStep = (
  props: ChallengesType,
): JSX.Element => {
  const {
    isRewardingGlobal,
    needCurrencyValue,
    needProof,
    challengeListProducts,
  } = props;

  const { t } = useTranslation();
  const rewardUnit = useSelector(productCurrencySelector);
  const resultUnit = getResultUnit(props);
  const unitToDisplay = isRewardingGlobal ? resultUnit : rewardUnit;
  const [{ value: products }] = useField(STEPS.products);
  const [{ value: answers }] = useField(STEPS.answers);

  const productsDataPreview = challengeListProducts?.reduce(
    (
      acc: TProductDetails[],
      product: IChallengeListProductItem,
    ): TProductDetails[] => {
      const summaryProduct = products[getProductFieldId(product)];
      const { isSelected } = summaryProduct ?? {};

      if (!isSelected) return acc;

      const { selectedUnits, selectedValue } = summaryProduct;

      return [
        ...acc,
        {
          ...product.challengeProduct,
          quantity: selectedUnits,
          ...(!!selectedValue && { financialValue: selectedValue }),
          productRewardValue: getProductRewardValuePreview(
            selectedUnits,
            product.threshold,
            product.reward,
          ),
          productResultValue: getProductResultValuePreview(
            product.coefficient,
            selectedUnits,
          ),
          threshold: product.threshold,
          coefficient: product.coefficient,
          reward: product.reward,
        },
      ];
    },
    [],
  );

  const totalDeclarationQuantityPreview = sumAll(
    productsDataPreview?.map(({ quantity = 0 }: TProductDetails) => quantity),
  );
  const totalDeclarationFinancialValuePreview = sumAll(
    productsDataPreview?.map(
      ({ financialValue = 0 }: TProductDetails) => financialValue,
    ),
  );
  const totalDeclarationRewardPreview = sumAll(
    productsDataPreview?.map(
      ({ productRewardValue = 0 }: TProductDetails) => productRewardValue,
    ),
  );
  const totalDeclarationResultPreview = sumAll(
    productsDataPreview?.map(
      ({ productResultValue = 0 }: TProductDetails) => productResultValue,
    ),
  );
  const questionsAnswered = answers?.length || 0;

  // if isGoalMecanicByProduct is true we have to display the potential rewards to be gained
  // in other cases, we have to display the results to be gained
  const isGoalMecanicByProduct = !isRewardingGlobal;

  return (
    <Layout
      title={t('challenges.declaration.summary.title')}
      subtitle={t('challenges.declaration.summary.subtitle')}
      step={STEPS.summary}
    >
      <FinalStepOverview
        products={productsDataPreview}
        questionsAnswered={questionsAnswered}
        totalDeclarationQuantityPreview={totalDeclarationQuantityPreview}
        totalDeclarationFinancialValuePreview={
          totalDeclarationFinancialValuePreview
        }
        totalDeclarationRewardPreview={totalDeclarationRewardPreview}
        totalDeclarationResultPreview={totalDeclarationResultPreview}
        displayFinancialValues={needCurrencyValue}
        isGoalMecanicByProduct={isGoalMecanicByProduct}
        unitToDisplay={unitToDisplay}
        isProofStepRequired={needProof}
        isQuestionsStepRequired={!!questionsAnswered}
      />
    </Layout>
  );
};
